import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { postWithDupCheck } from "@/lib/dupCheck";
import { afterAiFattura, OcrBadges } from "@/components/PagamentoMetodoDialog";
import { reviewMagazzinoItems } from "@/components/MagazzinoReview";
import { DdtRiferitiDialog } from "@/components/DdtRiferitiDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Receipt, Truck, Loader2, Zap, Sparkles, FileText, ScanLine, Plus as PlusIcon, Minus, AlertTriangle, Link2 } from "lucide-react";
import { toast } from "sonner";
import { compressImage } from "@/lib/image";
import BarcodeScanner from "@/components/BarcodeScanner";

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

const emptyFatt = {
  tipo: "acquisto",
  controparte: "",
  numero: "",
  data: new Date().toISOString().slice(0, 10),
  imponibile: 0,
  iva_percentuale: 22,
  stato: "da_pagare",
  data_scadenza: "",
  note: "",
  cantiere_id: null,
  fornitore_id: null,
  mezzo_pagamento: "bonifico",
  allegato_base64: null,
  allegato_mime: null,
  allegato_filename: null,
};
const emptyDDT = {
  numero: "",
  data: new Date().toISOString().slice(0, 10),
  descrizione: "",
  importo_stimato: 0,
  cantiere_id: null,
  fornitore_id: null,
  allegato_base64: null,
  allegato_mime: null,
  allegato_filename: null,
};

export default function QuickActions() {
  const [cantieri, setCantieri] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [openFatt, setOpenFatt] = useState(false);
  const [riferiti, setRiferiti] = useState(null);
  const [openDDT, setOpenDDT] = useState(false);
  const [fatt, setFatt] = useState(emptyFatt);
  const [ddt, setDdt] = useState(emptyDDT);
  const [busy, setBusy] = useState(false);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrResult, setOcrResult] = useState(null); // {tipo_rilevato, confidenza, fattura, ddt}
  const [chooseType, setChooseType] = useState(false); // modale "Cosa vuoi inserire?"
  const [attachBusy, setAttachBusy] = useState(false);
  const [batchQueue, setBatchQueue] = useState([]); // coda OCR multi-file
  // Scanner (Magazzino)
  const [scanOpen, setScanOpen] = useState(false);
  const [scanResult, setScanResult] = useState(null); // {code, item}
  const [associateOpen, setAssociateOpen] = useState(false);
  const [associateCode, setAssociateCode] = useState("");
  const attachRef = useRef();

  const load = async () => {
    try {
      const [c, f] = await Promise.all([
        api.get("/cantieri"),
        api.get("/fornitori").catch(() => ({ data: [] })),
      ]);
      setCantieri(c.data || []);
      setFornitori(f.data || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const saveFatt = async () => {
    if (!fatt.controparte.trim()) return toast.error("Inserisci la controparte");
    if (!(Number(fatt.imponibile) > 0)) return toast.error("Imponibile obbligatorio");
    setBusy(true);
    try {
      let magItems = fatt.magazzino_items;
      if (magItems?.length) { magItems = await reviewMagazzinoItems(magItems); if (magItems === null) return; }
      const r = await postWithDupCheck("/fatture", {
        ...fatt,
        magazzino_items: magItems,
        imponibile: Number(fatt.imponibile),
        iva_percentuale: Number(fatt.iva_percentuale),
        cantiere_id: fatt.cantiere_id || null,
        fornitore_id: fatt.fornitore_id || "",
      });
      if (!r) return;
      toast.success(r.data?.overwritten ? "Fattura sovrascritta" : "Fattura creata");
      await afterAiFattura(r.data, fatt);
      if (fatt.ddt_riferiti?.length) setRiferiti({ fattura: r.data, items: fatt.ddt_riferiti });
      setFatt(emptyFatt);
      setOpenFatt(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const saveDDT = async () => {
    if (!ddt.descrizione.trim() && !ddt.numero.trim()) return toast.error("Inserisci numero o descrizione");
    setBusy(true);
    try {
      let magItemsD = ddt.magazzino_items;
      if (magItemsD?.length) { magItemsD = await reviewMagazzinoItems(magItemsD); if (magItemsD === null) return; }
      const r = await postWithDupCheck("/ddt", {
        ...ddt,
        magazzino_items: magItemsD,
        importo_stimato: Number(ddt.importo_stimato || 0),
        cantiere_id: ddt.cantiere_id || null,
        fornitore_id: ddt.fornitore_id || null,
      });
      if (!r) return;
      toast.success(r.data?.overwritten ? "DDT sovrascritto" : "DDT creato");
      setDdt(emptyDDT);
      setOpenDDT(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const onPhoto = async (file) => {
    if (!file) return;
    return onAttachFiles([file]);
  };

  const confirmOcrType = (tipo) => {
    if (!ocrResult) return;
    const current = ocrResult;
    // L'allegato ORIGINALE (foto/PDF) deve essere conservato sul record creato
    const allegato = {
      allegato_base64: current.original_base64 || null,
      allegato_mime: current.original_mime || null,
      allegato_filename: current.original_filename || null,
    };
    if (tipo === "fattura") {
      const f = current.fattura || {};
      setFatt({
        ...emptyFatt,
        tipo: "acquisto",
        controparte: f.fornitore || "",
        numero: f.numero || "",
        data: /^\d{4}-\d{2}-\d{2}$/.test(f.data || "") ? f.data : emptyFatt.data,
        imponibile: Number(f.imponibile) || 0,
        iva_percentuale: Number(f.iva_percentuale) || 22,
        cantiere_id: f.cantiere_id || null,
        fornitore_id: f.fornitore_id || null,
        fornitore_partita_iva: f.fornitore_partita_iva || "",
        ddt_riferiti: f.ddt_riferiti || [],
        fornitore_extra: f.fornitore_anagrafica || null,
        stato: f.stato_pagamento === "pagata" ? "pagata" : emptyFatt.stato,
        data_pagamento: f.stato_pagamento === "pagata" && /^\d{4}-\d{2}-\d{2}$/.test(f.data || "") ? f.data : null,
        ai_pagato: f.stato_pagamento === "pagata",
        ai_metodo: f.metodo_pagamento || "",
        magazzino_items: f.magazzino_items?.length ? f.magazzino_items : null,
        note: f.descrizione || "",
        ...allegato,
      });
      setOcrResult(null);
      setOpenFatt(true);
    } else {
      const d = current.ddt || {};
      setDdt({
        ...emptyDDT,
        numero: d.numero || "",
        data: /^\d{4}-\d{2}-\d{2}$/.test(d.data || "") ? d.data : emptyDDT.data,
        descrizione: d.descrizione || "",
        importo_stimato: Number(d.importo_stimato) || 0,
        cantiere_id: d.cantiere_id || null,
        fornitore_id: d.fornitore_id || null,
        magazzino_items: d.magazzino_items?.length ? d.magazzino_items : null,
        ...allegato,
      });
      setOcrResult(null);
      setOpenDDT(true);
    }
    // Avanza nella coda OCR: dopo che l'utente salverà/chiuderà, mostra il prossimo
    if (batchQueue.length > 0) {
      const next = batchQueue[0];
      setBatchQueue(batchQueue.slice(1));
      // Breve delay per permettere al dialog corrente di aprirsi
      setTimeout(() => setOcrResult(next.data), 300);
    }
  };

  const onAttachFiles = async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    setAttachBusy(true);
    // Costruisce la coda OCR: per ogni file analizza + presenta il dialog di conferma Fattura/DDT
    const queue = [];
    for (const f of list) {
      try {
        // Comprime solo le immagini, i PDF passano as-is
        const isPdf = f.type === "application/pdf" || /\.pdf$/i.test(f.name || "");
        const payload = isPdf ? f : await compressImage(f, { maxDim: 1600, quality: 0.85 });
        toast.info(`Analisi AI: ${f.name}...`);
        const fd = new FormData();
        fd.append("file", payload, f.name);
        const { data } = await api.post("/documenti/ocr", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
        });
        queue.push({ data, fileName: f.name });
      } catch (e) {
        toast.error(`${f.name}: ${e?.response?.data?.detail || "OCR fallito"}`);
      }
    }
    setAttachBusy(false);
    if (attachRef.current) attachRef.current.value = "";
    if (queue.length === 0) return;
    // Carica il primo nel dialog di conferma; i successivi restano in coda
    setBatchQueue(queue.slice(1));
    setOcrResult(queue[0].data);
  };

  const chooseInsertType = (tipo) => {
    setChooseType(false);
    if (tipo === "fattura") setOpenFatt(true);
    else setOpenDDT(true);
  };

  // Barcode/QR scanner → cerca articolo magazzino
  const onCodeDetected = async (code) => {
    const c = (code || "").trim();
    if (!c) return;
    setScanOpen(false);
    try {
      const { data } = await api.get(`/magazzino/by-code/${encodeURIComponent(c)}`);
      setScanResult({ code: c, item: data });
    } catch (e) {
      if (e?.response?.status === 404) {
        setAssociateCode(c);
        setAssociateOpen(true);
      } else {
        toast.error(e?.response?.data?.detail || "Errore lettura codice");
      }
    }
  };

  const quickStock = async (itemId, delta, causale) => {
    try {
      await api.post(`/magazzino/${itemId}/movimento`, { delta, causale, note: "da scanner" });
      toast.success(`${delta > 0 ? "+" : ""}${delta} ${causale}`);
      setScanResult(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <Card className="card-elevated border-primary/30" data-testid="quick-actions-card">
      <CardContent className="p-4 md:p-5">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="h-4 w-4 text-primary" />
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Azioni rapide</div>
        </div>
        <div className="grid grid-cols-3 gap-2 md:gap-3">
          <input
            ref={attachRef}
            type="file"
            multiple
            accept="image/*,application/pdf"
            className="hidden"
            onChange={(e) => onAttachFiles(e.target.files)}
            data-testid="qa-attach-file"
          />
          <Button
            data-testid="qa-attach-btn"
            onClick={() => attachRef.current?.click()}
            disabled={attachBusy || ocrBusy}
            variant="outline"
            className="h-14 text-xs md:text-sm font-semibold border-primary/60 bg-primary/5 hover:bg-primary/10 flex-col md:flex-row gap-0.5 md:gap-2"
          >
            {(attachBusy || ocrBusy) ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5 text-primary" />}
            <span className="leading-tight">Scatta o Allega<br className="md:hidden" /> <span className="text-[10px] opacity-70 md:ml-1">con AI</span></span>
          </Button>
          <Button
            data-testid="qa-fatt-ddt"
            onClick={() => setChooseType(true)}
            className="h-14 text-xs md:text-sm font-semibold flex-col md:flex-row gap-0.5 md:gap-2"
          >
            <Receipt className="h-5 w-5" /> <span className="leading-tight">Fatture<br className="md:hidden" /> e DDT</span>
          </Button>
          <Button
            data-testid="qa-scan-btn"
            onClick={() => setScanOpen(true)}
            variant="outline"
            className="h-14 text-xs md:text-sm font-semibold border-primary/60 bg-primary/5 hover:bg-primary/10 flex-col md:flex-row gap-0.5 md:gap-2"
          >
            <ScanLine className="h-5 w-5 text-primary" />
            <span className="leading-tight">Scansiona<br className="md:hidden" /> QR/Barcode</span>
          </Button>
        </div>
      </CardContent>

      {/* Scanner magazzino */}
      <BarcodeScanner open={scanOpen} onClose={() => setScanOpen(false)} onDetected={onCodeDetected} />

      {/* Risultato scansione: giacenza + bottoni carico/scarico */}
      <DdtRiferitiDialog fattura={riferiti?.fattura} items={riferiti?.items} onClose={() => setRiferiti(null)} />
      <Dialog open={!!scanResult} onOpenChange={(o) => !o && setScanResult(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ScanLine className="h-5 w-5 text-primary" /> Articolo scansionato</DialogTitle></DialogHeader>
          {scanResult && (
            <div className="space-y-3">
              <div className="rounded-lg bg-secondary p-3">
                <div className="text-[11px] font-mono text-muted-foreground">{scanResult.code}</div>
                <div className="text-lg font-extrabold">{scanResult.item.nome}</div>
                <div className="text-xs text-muted-foreground">{scanResult.item.categoria || "—"}</div>
                <div className="mt-2 flex items-end justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Giacenza</div>
                    <div className="text-2xl font-extrabold tabular-nums">
                      {scanResult.item.quantita} <span className="text-sm font-normal text-muted-foreground">{scanResult.item.unita_misura}</span>
                    </div>
                  </div>
                  {Number(scanResult.item.soglia_minima || 0) > 0 &&
                   Number(scanResult.item.quantita || 0) <= Number(scanResult.item.soglia_minima || 0) && (
                    <span className="text-[11px] font-bold text-amber-700 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> sotto soglia
                    </span>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="qa-scan-carico" onClick={() => quickStock(scanResult.item.id, 1, "carico")} className="h-14 flex-col gap-0">
                  <PlusIcon className="h-5 w-5" /> <span className="text-xs">Carico +1</span>
                </Button>
                <Button data-testid="qa-scan-scarico" variant="outline" onClick={() => quickStock(scanResult.item.id, -1, "scarico")} className="h-14 flex-col gap-0">
                  <Minus className="h-5 w-5" /> <span className="text-xs">Scarico −1</span>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Codice sconosciuto */}
      <Dialog open={associateOpen} onOpenChange={setAssociateOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Link2 className="h-5 w-5 text-primary" /> Codice non riconosciuto</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded bg-secondary p-2 text-sm">
              Codice letto: <strong className="font-mono">{associateCode}</strong>
            </div>
            <div className="text-xs text-muted-foreground">
              Nessun articolo in magazzino usa questo codice. Vai alla sezione <strong>Magazzino</strong> per associarlo a un articolo esistente o crearne uno nuovo.
            </div>
            <Button data-testid="qa-go-magazzino" onClick={() => {
              setAssociateOpen(false);
              window.location.href = "/magazzino";
            }} className="w-full">
              Vai a Magazzino
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modale scelta tipo documento */}
      <Dialog open={chooseType} onOpenChange={setChooseType}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Cosa vuoi inserire?</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <Button
              data-testid="qa-choose-fattura"
              onClick={() => chooseInsertType("fattura")}
              className="h-24 flex-col gap-2"
            >
              <Receipt className="h-7 w-7" />
              <div className="font-bold text-base">Fattura</div>
              <div className="text-[10px] opacity-80 font-normal">Acquisto o vendita con IVA</div>
            </Button>
            <Button
              data-testid="qa-choose-ddt"
              onClick={() => chooseInsertType("ddt")}
              variant="outline"
              className="h-24 flex-col gap-2 border-primary/60"
            >
              <Truck className="h-7 w-7 text-primary" />
              <div className="font-bold text-base">DDT</div>
              <div className="text-[10px] opacity-80 font-normal">Bolla di trasporto fornitore</div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma tipo OCR */}
      <Dialog open={!!ocrResult} onOpenChange={(o) => !o && setOcrResult(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Che documento è?</DialogTitle></DialogHeader>
          {ocrResult && (
            <div className="space-y-3">
              <div className="bg-secondary rounded-lg p-3 text-sm space-y-1">
                <div className="text-xs text-muted-foreground">L'AI ha riconosciuto:</div>
                <div className="font-bold text-lg flex items-center gap-2">
                  {ocrResult.tipo_rilevato === "fattura" ? <><Receipt className="h-5 w-5 text-primary" /> Fattura</> : <><Truck className="h-5 w-5 text-primary" /> DDT (bolla)</>}
                  <span className="text-xs font-normal text-muted-foreground">({ocrResult.confidenza || 0}% confidenza)</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {ocrResult.tipo_rilevato === "fattura"
                    ? `Fornitore: ${ocrResult.fattura?.fornitore || "—"} · Numero: ${ocrResult.fattura?.numero || "—"} · Totale: ${fmt(ocrResult.fattura?.totale || 0)}`
                    : `Numero: ${ocrResult.ddt?.numero || "—"} · ${ocrResult.ddt?.descrizione?.slice(0, 60) || "—"}`}
                </div>
                {ocrResult.tipo_rilevato === "fattura" && <OcrBadges o={ocrResult.fattura} />}
              </div>
              <div className="text-xs text-muted-foreground">Conferma o cambia il tipo:</div>
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="qa-ocr-confirm-fattura" variant={ocrResult.tipo_rilevato === "fattura" ? "default" : "outline"} onClick={() => confirmOcrType("fattura")}>
                  <Receipt className="h-4 w-4 mr-2" /> È una Fattura
                </Button>
                <Button data-testid="qa-ocr-confirm-ddt" variant={ocrResult.tipo_rilevato === "ddt" ? "default" : "outline"} onClick={() => confirmOcrType("ddt")}>
                  <FileText className="h-4 w-4 mr-2" /> È un DDT
                </Button>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setOcrResult(null)} className="w-full">Annulla</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={openFatt} onOpenChange={setOpenFatt}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Receipt className="h-5 w-5 text-primary" />Nuova Fattura</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipo</Label>
              <Select value={fatt.tipo} onValueChange={(v) => setFatt({ ...fatt, tipo: v })}>
                <SelectTrigger data-testid="qa-fatt-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="acquisto">Acquisto (fornitore)</SelectItem>
                  <SelectItem value="vendita">Vendita (cliente)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Controparte</Label>
              <Input data-testid="qa-fatt-controparte" value={fatt.controparte} onChange={(e) => setFatt({ ...fatt, controparte: e.target.value })} />
            </div>
            {fatt.tipo === "acquisto" && fornitori.length > 0 && (
              <div className="col-span-2">
                <Label>Fornitore (opzionale)</Label>
                <Select value={fatt.fornitore_id || "none"} onValueChange={(v) => {
                  if (v === "none") return setFatt({ ...fatt, fornitore_id: null });
                  const f = fornitori.find((x) => x.id === v);
                  setFatt({ ...fatt, fornitore_id: v, controparte: f?.nome || fatt.controparte });
                }}>
                  <SelectTrigger data-testid="qa-fatt-fornitore"><SelectValue placeholder="Seleziona dalla rubrica" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Nessuno —</SelectItem>
                    {fornitori.map((f) => <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Numero</Label><Input value={fatt.numero} onChange={(e) => setFatt({ ...fatt, numero: e.target.value })} /></div>
            <div><Label>Data</Label><Input type="date" value={fatt.data} onChange={(e) => setFatt({ ...fatt, data: e.target.value })} /></div>
            <div><Label>Imponibile €</Label><Input data-testid="qa-fatt-imponibile" type="number" step="0.01" value={fatt.imponibile} onChange={(e) => setFatt({ ...fatt, imponibile: e.target.value })} /></div>
            <div><Label>IVA %</Label><Input type="number" step="0.1" value={fatt.iva_percentuale} onChange={(e) => setFatt({ ...fatt, iva_percentuale: e.target.value })} /></div>
            <div className="col-span-2">
              <Label>Cantiere</Label>
              <Select value={fatt.cantiere_id || "none"} onValueChange={(v) => setFatt({ ...fatt, cantiere_id: v === "none" ? null : v })}>
                <SelectTrigger data-testid="qa-fatt-cantiere"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Nessuno —</SelectItem>
                  {cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Scadenza</Label><Input type="date" value={fatt.data_scadenza} onChange={(e) => setFatt({ ...fatt, data_scadenza: e.target.value })} /></div>
            <div>
              <Label>Stato</Label>
              <Select value={fatt.stato} onValueChange={(v) => setFatt({ ...fatt, stato: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="da_pagare">Da pagare</SelectItem>
                  <SelectItem value="parziale">Parziale</SelectItem>
                  <SelectItem value="pagata">Pagata</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 bg-secondary rounded-lg p-2 text-xs text-muted-foreground">
              Totale calcolato: <strong>€ {(Number(fatt.imponibile || 0) * (1 + Number(fatt.iva_percentuale || 0) / 100)).toFixed(2)}</strong>
            </div>
          </div>
          <Button data-testid="qa-fatt-save" onClick={saveFatt} disabled={busy} className="h-11 mt-2">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Receipt className="h-4 w-4 mr-2" />}
            Salva fattura
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={openDDT} onOpenChange={setOpenDDT}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Truck className="h-5 w-5 text-primary" />Nuovo DDT</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Numero DDT</Label><Input data-testid="qa-ddt-numero" value={ddt.numero} onChange={(e) => setDdt({ ...ddt, numero: e.target.value })} /></div>
            <div><Label>Data</Label><Input type="date" value={ddt.data} onChange={(e) => setDdt({ ...ddt, data: e.target.value })} /></div>
            {fornitori.length > 0 && (
              <div className="col-span-2">
                <Label>Fornitore</Label>
                <Select value={ddt.fornitore_id || "none"} onValueChange={(v) => setDdt({ ...ddt, fornitore_id: v === "none" ? null : v })}>
                  <SelectTrigger data-testid="qa-ddt-fornitore"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Da assegnare —</SelectItem>
                    {fornitori.map((f) => <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="col-span-2">
              <Label>Cantiere</Label>
              <Select value={ddt.cantiere_id || "none"} onValueChange={(v) => setDdt({ ...ddt, cantiere_id: v === "none" ? null : v })}>
                <SelectTrigger data-testid="qa-ddt-cantiere"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Da assegnare —</SelectItem>
                  {cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Descrizione materiali</Label>
              <Textarea data-testid="qa-ddt-descr" rows={3} value={ddt.descrizione} onChange={(e) => setDdt({ ...ddt, descrizione: e.target.value })} placeholder="Es: 50 sacchi cemento, 2 bancali mattoni..." />
            </div>
            <div className="col-span-2">
              <Label>Importo stimato € (se noto)</Label>
              <Input type="number" step="0.01" value={ddt.importo_stimato} onChange={(e) => setDdt({ ...ddt, importo_stimato: e.target.value })} />
            </div>
          </div>
          <Button data-testid="qa-ddt-save" onClick={saveDDT} disabled={busy} className="h-11 mt-2">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Truck className="h-4 w-4 mr-2" />}
            Salva DDT
          </Button>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
